import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { format, startOfWeek, endOfWeek, addWeeks, startOfMonth, endOfMonth, addMonths, isSameDay } from "date-fns";
import { vi } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/teacher/schedule")({ component: TeacherSchedule });

const CLASS_COLORS = [
  { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200" },
  { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
  { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-200" },
  { bg: "bg-violet-100", text: "text-violet-800", border: "border-violet-200" },
  { bg: "bg-pink-100", text: "text-pink-800", border: "border-pink-200" },
  { bg: "bg-cyan-100", text: "text-cyan-800", border: "border-cyan-200" },
];

type ClassRow = { id: string; name: string; subject: string | null; class_code: string | null };
type SessionRow = {
  id: string;
  starts_at: string;
  ends_at: string;
  room: string | null;
  class_id: string;
  topic: string | null;
  class?: ClassRow;
  studentCount: number;
};

function TeacherSchedule() {
  const { user } = useAuth();
  const [viewMode, setViewMode] = React.useState<"week" | "month">("week");
  const [offset, setOffset] = React.useState(0);

  const now = new Date();
  const rangeStart = viewMode === "week"
    ? startOfWeek(addWeeks(now, offset), { weekStartsOn: 1 })
    : startOfMonth(addMonths(now, offset));
  const rangeEnd = viewMode === "week"
    ? endOfWeek(addWeeks(now, offset), { weekStartsOn: 1 })
    : endOfMonth(addMonths(now, offset));

  const scheduleQ = useQuery({
    queryKey: ["teacher-schedule", user?.id, viewMode, offset],
    enabled: !!user,
    queryFn: async (): Promise<{ sessions: SessionRow[]; classes: ClassRow[] }> => {
      const { data: classes } = await supabase
        .from("classes").select("id, name, subject, class_code").eq("teacher_id", user!.id);
      const classIds = (classes ?? []).map(c => c.id);
      if (!classIds.length) return { sessions: [], classes: [] };

      const { data: sessions } = await supabase
        .from("class_sessions")
        .select("id, starts_at, ends_at, room, class_id, topic")
        .in("class_id", classIds)
        .gte("starts_at", rangeStart.toISOString())
        .lte("starts_at", rangeEnd.toISOString())
        .order("starts_at");

      const { data: enrolls } = await supabase
        .from("enrollments").select("class_id").in("class_id", classIds).eq("is_active", true);
      const countMap = new Map<string, number>();
      (enrolls ?? []).forEach(e => countMap.set(e.class_id, (countMap.get(e.class_id) ?? 0) + 1));

      return {
        sessions: ((sessions ?? []) as unknown as Array<{ id: string; starts_at: string; ends_at: string; room: string | null; class_id: string; topic: string | null }>).map(s => ({
          ...s,
          topic: s.topic ?? null,
          class: (classes ?? []).find(c => c.id === s.class_id),
          studentCount: countMap.get(s.class_id) ?? 0,
        })),
        classes: (classes ?? []),
      };
    },
  });

  const colorMap = React.useMemo(() => {
    const map = new Map<string, typeof CLASS_COLORS[0]>();
    (scheduleQ.data?.classes ?? []).forEach((c, i) => {
      map.set(c.id, CLASS_COLORS[i % CLASS_COLORS.length]);
    });
    return map;
  }, [scheduleQ.data?.classes]);

  const dayGroups = React.useMemo(() => {
    const groups = new Map<string, SessionRow[]>();
    (scheduleQ.data?.sessions ?? []).forEach(s => {
      const dayKey = format(new Date(s.starts_at), "yyyy-MM-dd");
      const arr = groups.get(dayKey) ?? [];
      arr.push(s);
      groups.set(dayKey, arr);
    });
    return groups;
  }, [scheduleQ.data?.sessions]);

  const weekDays = viewMode === "week"
    ? Array.from({ length: 7 }, (_, i) => {
        const d = new Date(rangeStart);
        d.setDate(d.getDate() + i);
        return d;
      })
    : [];

  const rangeLabel = viewMode === "week"
    ? `${format(rangeStart, "dd/MM")} – ${format(rangeEnd, "dd/MM/yyyy")}`
    : format(rangeStart, "MMMM yyyy", { locale: vi });

  return (
    <PortalLayout portal="teacher" title="Lịch dạy"
      breadcrumb={[{ label: "Giáo viên", to: "/teacher" }, { label: "Lịch dạy" }]}>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={viewMode} onValueChange={(v) => { setViewMode(v as "week" | "month"); setOffset(0); }}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="week">Theo tuần</SelectItem>
            <SelectItem value="month">Theo tháng</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="size-8" onClick={() => setOffset(o => o - 1)}>
            <ChevronLeft className="size-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setOffset(0)}>Hôm nay</Button>
          <Button variant="outline" size="icon" className="size-8" onClick={() => setOffset(o => o + 1)}>
            <ChevronRight className="size-4" />
          </Button>
        </div>

        <span className="text-sm font-medium capitalize">{rangeLabel}</span>

        <Badge variant="secondary" className="text-xs ml-auto">
          {scheduleQ.data?.sessions.length ?? 0} buổi
        </Badge>
      </div>

      {viewMode === "week" && (
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map(day => {
            const dayKey = format(day, "yyyy-MM-dd");
            const isToday = isSameDay(day, new Date());
            const sessions = dayGroups.get(dayKey) ?? [];
            return (
              <div key={dayKey} className={`rounded-xl border p-2 min-h-[140px] ${isToday ? "ring-2" : ""}`}
                style={isToday ? { ["--tw-ring-color" as never]: "var(--portal)" } : {}}>
                <div className={`text-xs text-center mb-2 font-medium ${isToday ? "text-[var(--portal)]" : "text-muted-foreground"}`}>
                  {format(day, "EEE", { locale: vi })}
                  <div className={`text-lg ${isToday ? "font-bold" : ""}`}>{format(day, "dd")}</div>
                </div>
                <div className="space-y-1.5">
                  {sessions.map(s => {
                    const color = colorMap.get(s.class_id) ?? CLASS_COLORS[0];
                    const isPast = new Date(s.starts_at) < new Date();
                    return (
                      <Popover key={s.id}>
                        <PopoverTrigger asChild>
                          <button className={`w-full text-left rounded-lg p-2 border transition-opacity ${color.bg} ${color.border} ${color.text} text-[11px] leading-tight ${isPast ? "opacity-50 border-dashed" : "hover:opacity-80"}`}>
                            <div className="font-medium truncate">{s.class?.name}</div>
                            <div className="opacity-70">{format(new Date(s.starts_at), "HH:mm")}–{format(new Date(s.ends_at), "HH:mm")}</div>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-3" side="right">
                          <div className="space-y-2">
                            <div className="font-semibold">{s.class?.name}</div>
                            <div className="text-xs text-muted-foreground space-y-1">
                              {s.class?.class_code && <div>Mã: <code className="bg-muted px-1 rounded">{s.class.class_code}</code></div>}
                              {s.class?.subject && <div>Môn: {s.class.subject}</div>}
                              <div>Giờ: {format(new Date(s.starts_at), "HH:mm")} – {format(new Date(s.ends_at), "HH:mm")}</div>
                              {s.room && <div>Phòng: {s.room}</div>}
                              <div>Sĩ số: {s.studentCount} học sinh</div>
                              {s.topic && <div>{s.topic}</div>}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                  {sessions.length === 0 && (
                    <div className="text-[10px] text-muted-foreground text-center opacity-50 py-2">—</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewMode === "month" && (
        <div className="space-y-3">
          {(scheduleQ.data?.sessions.length ?? 0) === 0 ? (
            <div className="text-center py-12">
              <div className="text-3xl mb-2">📅</div>
              <div className="text-sm text-muted-foreground">Không có buổi dạy nào trong tháng này.</div>
            </div>
          ) : (
            (() => {
              const todayStart = new Date(); todayStart.setHours(0,0,0,0);
              const sortedEntries = [...dayGroups.entries()].sort(([a], [b]) => {
                const aFuture = new Date(a) >= todayStart;
                const bFuture = new Date(b) >= todayStart;
                if (aFuture && !bFuture) return -1;
                if (!aFuture && bFuture) return 1;
                return a.localeCompare(b);
              });
              return sortedEntries.map(([dayKey, sessions]) => {
                const dayIsPast = new Date(dayKey) < todayStart;
                return (
              <div key={dayKey} className={dayIsPast ? "opacity-60" : ""}>
                <div className="text-xs font-medium text-muted-foreground mb-2 capitalize flex items-center">
                  {format(new Date(dayKey), "EEEE, dd/MM/yyyy", { locale: vi })}
                  {isSameDay(new Date(dayKey), new Date()) && <Badge className="ml-2 text-[9px]" style={{ background: "var(--portal)" }}>Hôm nay</Badge>}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {sessions.map(s => {
                    const color = colorMap.get(s.class_id) ?? CLASS_COLORS[0];
                    const isPast = new Date(s.starts_at) < new Date();
                    return (
                      <div key={s.id} className={`rounded-xl border p-3 ${color.bg} ${color.border} ${isPast ? "border-dashed opacity-60" : ""}`}>
                        <div className={`font-semibold text-sm ${color.text}`}>{s.class?.name}</div>
                        <div className={`text-xs ${color.text} opacity-70 mt-1`}>
                          {format(new Date(s.starts_at), "HH:mm")}–{format(new Date(s.ends_at), "HH:mm")}
                          {s.room ? ` · P.${s.room}` : ""}
                          {` · ${s.studentCount} HS`}
                        </div>
                        {s.topic && <div className={`text-xs ${color.text} opacity-80 mt-1 truncate`}>{s.topic}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
                );
              });
            })()
          )}
        </div>
      )}
    </PortalLayout>
  );
}
